/**
 * WorkflowTemplateLibrary - Pre-built workflow templates (T7.2)
 *
 * Grid of template cards. Click to populate NL input and pre-fill steps.
 *
 * Patent: Natural Language Workflow Builder for AI-Governed Systems
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useState } from 'react';
import { FiX, FiSearch, FiLayout } from 'react-icons/fi';
import type { WorkflowTemplate } from './types';

const BUILT_IN_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'security-review',
    name: 'Security Review Pipeline',
    description: 'Comprehensive security scan with threat modeling, vulnerability assessment, and remediation report.',
    category: 'security',
    nlPrompt: 'First, perform a threat model analysis on the target system. Then scan for security vulnerabilities across the codebase. Run penetration testing against the identified attack surfaces. Generate a remediation report with prioritized findings.',
    steps: [
      { order: 1, description: 'Threat model analysis', confidence: 0.9 },
      { order: 2, description: 'Vulnerability scanning', confidence: 0.9 },
      { order: 3, description: 'Penetration testing', confidence: 0.85 },
      { order: 4, description: 'Remediation report generation', confidence: 0.8 },
    ],
    popularity: 95,
  },
  {
    id: 'compliance-check',
    name: 'Compliance Assessment',
    description: 'Standards compliance validation with gap analysis and corrective action plan.',
    category: 'compliance',
    nlPrompt: 'Audit the current system against our compliance standards. Identify any gaps or violations. Generate a corrective action plan for each finding. Create a compliance report for stakeholders.',
    steps: [
      { order: 1, description: 'Standards compliance audit', confidence: 0.85 },
      { order: 2, description: 'Gap analysis', confidence: 0.8 },
      { order: 3, description: 'Corrective action planning', confidence: 0.75 },
      { order: 4, description: 'Compliance report generation', confidence: 0.8 },
    ],
    popularity: 88,
  },
  {
    id: 'deploy-validate',
    name: 'Deploy & Validate',
    description: 'Full deployment pipeline with pre-flight checks, deployment, and post-deployment validation.',
    category: 'deployment',
    nlPrompt: 'Run pre-flight validation checks on the deployment package. Deploy the application to the target environment. Execute post-deployment smoke tests. Monitor the deployment for any regressions or performance issues.',
    steps: [
      { order: 1, description: 'Pre-flight validation', confidence: 0.9 },
      { order: 2, description: 'Application deployment', confidence: 0.85 },
      { order: 3, description: 'Post-deployment smoke tests', confidence: 0.8 },
      { order: 4, description: 'Deployment monitoring', confidence: 0.75 },
    ],
    popularity: 92,
  },
  {
    id: 'data-privacy',
    name: 'Privacy Impact Assessment',
    description: 'GDPR/CCPA privacy review with data mapping, risk assessment, and DPO report.',
    category: 'privacy',
    nlPrompt: 'Map all personal data flows in the system. Assess privacy risks and identify high-risk processing activities. Check consent mechanisms and data subject rights fulfillment. Generate a privacy impact assessment report.',
    steps: [
      { order: 1, description: 'Personal data flow mapping', confidence: 0.85 },
      { order: 2, description: 'Privacy risk assessment', confidence: 0.8 },
      { order: 3, description: 'Consent mechanism review', confidence: 0.75 },
      { order: 4, description: 'PIA report generation', confidence: 0.8 },
    ],
    popularity: 78,
  },
  {
    id: 'code-quality',
    name: 'Code Quality Pipeline',
    description: 'Automated code review with linting, testing, and quality metrics report.',
    category: 'development',
    nlPrompt: 'Analyze the codebase for quality issues and code smells. Run the test suite and check coverage. Validate against our coding standards. Generate a quality report with improvement recommendations.',
    steps: [
      { order: 1, description: 'Code quality analysis', confidence: 0.85 },
      { order: 2, description: 'Test suite execution', confidence: 0.9 },
      { order: 3, description: 'Standards validation', confidence: 0.8 },
      { order: 4, description: 'Quality report generation', confidence: 0.8 },
    ],
    popularity: 85,
  },
  {
    id: 'incident-response',
    name: 'Incident Response',
    description: 'Automated incident triage, investigation, and resolution workflow.',
    category: 'operations',
    nlPrompt: 'Triage the incoming incident and assess severity. Investigate the root cause using logs and metrics. Implement a fix or mitigation. Generate an incident report and notify stakeholders.',
    steps: [
      { order: 1, description: 'Incident triage and severity assessment', confidence: 0.85 },
      { order: 2, description: 'Root cause investigation', confidence: 0.8 },
      { order: 3, description: 'Fix implementation or mitigation', confidence: 0.75 },
      { order: 4, description: 'Incident report and notification', confidence: 0.8 },
    ],
    popularity: 82,
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  security: 'bg-red-100 text-red-700',
  compliance: 'bg-blue-100 text-blue-700',
  deployment: 'bg-green-100 text-green-700',
  privacy: 'bg-purple-100 text-purple-700',
  development: 'bg-amber-100 text-amber-700',
  operations: 'bg-indigo-100 text-indigo-700',
};

interface WorkflowTemplateLibraryProps {
  onSelect: (template: WorkflowTemplate) => void;
  onClose: () => void;
}

const WorkflowTemplateLibrary: React.FC<WorkflowTemplateLibraryProps> = ({ onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [...new Set(BUILT_IN_TEMPLATES.map(t => t.category))];

  const filtered = BUILT_IN_TEMPLATES.filter(t => {
    const matchesSearch = !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => b.popularity - a.popularity);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiLayout className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">Workflow Templates</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Search + Filters */}
        <div className="px-6 py-3 border-b border-gray-100 space-y-2">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors
                ${!selectedCategory ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors capitalize
                  ${selectedCategory === cat ? CATEGORY_COLORS[cat] || 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Template grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map(template => (
              <button
                key={template.id}
                onClick={() => onSelect(template)}
                className="text-left border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-colors group"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-900">
                    {template.name}
                  </h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${CATEGORY_COLORS[template.category] || 'bg-gray-100 text-gray-600'}`}>
                    {template.category}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-3">{template.description}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{template.steps.length} steps</span>
                  <span className="text-gray-300">|</span>
                  <span>Used {template.popularity}+ times</span>
                </div>
              </button>
            ))}

            {filtered.length === 0 && (
              <div className="col-span-2 text-center py-8 text-gray-500">
                <p className="text-sm">No templates match your search.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTemplateLibrary;
